import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Clock, ChevronDown, User, LogIn, Car } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { RenaultEmblem } from "@/components/SplashScreen";
import useSiteInfo, { hoursLines, telHref } from "@/hooks/useSiteInfo";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const site = useSiteInfo();
  const [features, setFeatures] = useState({
    feature_fahrzeuge: true,
    feature_aktionen: true,
    feature_termine: true,
    feature_bewertungen: true,
    feature_kontakt: true,
    feature_reifenlager: true,
    feature_kundenportal: true
  });

  const [customPages, setCustomPages] = useState([]);

  useEffect(() => {
    const loadFeatures = async () => {
      try {
        const res = await axios.get(`${API}/settings`);
        setFeatures({
          feature_fahrzeuge: res.data.feature_fahrzeuge ?? true,
          feature_aktionen: res.data.feature_aktionen ?? true,
          feature_termine: res.data.feature_termine ?? true,
          feature_bewertungen: res.data.feature_bewertungen ?? true,
          feature_kontakt: res.data.feature_kontakt ?? true,
          feature_reifenlager: res.data.feature_reifenlager ?? true,
          feature_kundenportal: res.data.feature_kundenportal ?? true
        });
      } catch (error) {
        console.error("Error loading features:", error);
      }
    };
    loadFeatures();
    axios.get(`${API}/pages`).then((r) => setCustomPages(r.data.filter((p) => p.in_menu))).catch(() => {});
    const interval = setInterval(loadFeatures, 10000);
    return () => clearInterval(interval);
  }, []);

  const isActive = (path) => location.pathname === path;

  // Filter navLinks based on features
  const allNavLinks = [
    { path: "/", label: "Startseite", feature: null },
    { path: "/fahrzeuge", label: "Fahrzeuge", feature: "feature_fahrzeuge" },
    { path: "/aktionen", label: "Aktionen", feature: "feature_aktionen" },
    { path: "/termin", label: "Termin buchen", feature: "feature_termine" },
    { path: "/bewertungen", label: "Bewertungen", feature: "feature_bewertungen" },
    { path: "/kontakt", label: "Kontakt", feature: null },
  ];

  const navLinks = [
    ...allNavLinks.filter(link => link.feature === null || features[link.feature]),
    ...customPages.map((p) => ({ path: `/seite/${p.slug}`, label: p.title, feature: null })),
  ];

  const services = [
    "Inspektion & Wartung",
    "Reparaturen",
    "TÜV & HU",
    "Klimaservice",
    "Reifenservice",
  ];

  return (
    <>
      {/* Top Bar - Renault Black */}
      <div className="bg-black text-white py-2 hidden md:block">
        <div className="max-w-[1216px] mx-auto px-8">
          <div className="flex justify-between items-center text-sm font-read">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[#EFDF00]" />
                <a href={telHref(site.company_phone)} className="hover:text-[#EFDF00] transition-colors" data-testid="header-phone">{site.company_phone}</a>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#EFDF00]" />
                <span data-testid="header-hours">{hoursLines(site)[0]}</span>
              </div>
            </div>
            <div className="flex items-center gap-4">
              {/* User Login/Profile Button */}
              {features.feature_kundenportal && !authLoading && (
                isAuthenticated ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="text-white hover:text-[#EFDF00] gap-2">
                        <User className="w-4 h-4" />
                        <span className="hidden sm:inline">{user?.name?.split(" ")[0]}</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem asChild>
                        <Link to="/meine-garage" className="flex items-center gap-2 cursor-pointer">
                          <Car className="w-4 h-4" />
                          Meine Garage
                        </Link>
                      </DropdownMenuItem>
                      {features.feature_termine && (
                      <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem asChild>
                        <Link to="/termin" className="flex items-center gap-2 cursor-pointer">
                          <Clock className="w-4 h-4" />
                          Termin buchen
                        </Link>
                      </DropdownMenuItem>
                      </>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link to="/login">
                    <Button variant="ghost" size="sm" className="text-white hover:text-[#EFDF00] gap-2">
                      <LogIn className="w-4 h-4" />
                      <span className="hidden sm:inline">Anmelden</span>
                    </Button>
                  </Link>
                )
              )}
              <span className="text-[#BBBCBC]">Ihr Renault & Dacia Partner</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Header - Renault White */}
      <header className="bg-white sticky top-0 z-50 border-b border-[#D9D9D6]">
        <div className="max-w-[1216px] mx-auto px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo mit Renault Emblem */}
            <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
              <div className="w-12 h-12 bg-[#EFDF00] flex items-center justify-center p-2">
                <RenaultEmblem size={32} variant="dark" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-xl font-nouvel font-bold text-black tracking-tight text-left">
                  AUTO THURM
                </h1>
                <p className="text-xs text-[#656666] font-read">Renault & Dacia</p>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`text-sm font-read font-medium transition-colors ${
                    isActive(link.path)
                      ? "text-black"
                      : "text-[#656666] hover:text-black"
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Services Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button 
                    className="flex items-center gap-1 text-sm font-read font-medium text-[#656666] hover:text-black transition-colors"
                    data-testid="services-dropdown"
                  >
                    Werkstatt
                    <ChevronDown className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  {services.map((service) => (
                    <DropdownMenuItem key={service} className="font-read">
                      <Link to="/termin" className="w-full">{service}</Link>
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>

            {/* CTA Button - Renault Standards: 46px height, 16px/11px padding */}
            <div className="hidden lg:flex items-center gap-4">
              <Link to="/termin">
                <Button 
                  className="h-[46px] px-4 py-[11px] bg-[#EFDF00] text-black hover:bg-[#F8EB4C] font-read font-bold text-base rounded-none"
                  data-testid="header-termin-btn"
                >
                  Termin vereinbaren
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Menu öffnen"
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-black" />
              ) : (
                <Menu className="w-6 h-6 text-black" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        <div
          className={`lg:hidden ${
            mobileMenuOpen ? "block" : "hidden"
          } bg-white border-t`}
        >
          <div className="px-4 py-4 space-y-3">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={`block py-2 px-4  text-sm font-medium ${
                  isActive(link.path)
                    ? "bg-[#EBEBEB] text-black"
                    : "text-#888B8D hover:bg-[#F5F5F5]"
                }`}
                onClick={() => setMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-3 border-t">
              <p className="text-xs text-#888B8D px-4 mb-2">Werkstatt-Services</p>
              {services.map((service) => (
                <Link
                  key={service}
                  to="/termin"
                  className="block py-2 px-4 text-sm text-#888B8D hover:bg-[#F5F5F5] "
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {service}
                </Link>
              ))}
            </div>
            <div className="pt-3">
              <Link to="/termin" onClick={() => setMobileMenuOpen(false)}>
                <Button className="w-full bg-[#EFDF00] text-black hover:bg-#EFDF00 font-bold">
                  Termin vereinbaren
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;
